import React, { useState, useEffect, Fragment } from "react";
import "./index.scss";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Host from "../../Components/Host";
import { useGetUserDataQuery } from "../../services/login";
import { useSelector } from "react-redux";
import HelmetComponent from "../../Components/HelmetComponent";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import { getMetaByCountry } from "../../metaConfig";
import formatPrice from "../../Components/formatPrice";

function FAQS() {
  const { userId, userToken, countryCode } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const { data: userData } = useGetUserDataQuery(
    { userId: userId, userToken: userToken, countryCode: countryCode },
    { count: 1 },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      // behavior: "smooth"
    });
  }, []);

  const [isMetaTitle, setMetaTitle] = useState("");
  useEffect(() => {
    const { title } = getMetaByCountry(countryCode);
    setMetaTitle(title);
  }, [countryCode]);

 
  const faqs = [
    {
      question: "Where do you ship?",
      answer: `We currently deliver orders within the ${userData?.contant?.country || ""} through ${window.location.hostname}. If delivery to other countries is available, the available delivery country, currency, delivery fees, and shipping options will be shown on the website or at checkout before the order is placed.`,
    },
    {
      question: "How long does it typically take for products to be dispatched?",
      answer:
        "Orders are usually processed and dispatched within 1 to 2 working days after order confirmation. Working days are Monday to Friday, excluding UAE public holidays. Orders placed outside working hours, during weekends, or on UAE public holidays may be processed on the next working day.",
    },
    {
      question: "How long does it typically take for my purchase to arrive once it is dispatched?",
      answer:
        "After dispatch, shipments are generally delivered within 2 to 8 working days, depending on the delivery location, courier, product type, and operational conditions. The estimated total delivery time is usually 3 to 10 working days from order confirmation. Some items may take longer if they require special logistics, additional handling, supplier-side processing, or courier arrangements.",
    },
    {
      question: "Are all products listed on your website in stock?",
      answer:
        "We make reasonable efforts to keep product availability accurate and updated on our website. Products that can be added to cart are generally available for order. However, in rare cases, an item may become unavailable after an order is placed due to supplier-side stock changes, fulfillment limitations, system updates, or other operational reasons. If this happens, we will contact the customer promptly and offer one or more of the following options: wait for the product to become available, choose an alternative product, or cancel the affected item and receive a full refund for that item. We do not charge customers any additional amount after order confirmation without the customer's express approval.",
    },
    {
      question: "Do I need to open an account to shop on your website?",
      answer:
        "No. You do not need to open an account to place an order on our website. You may checkout as a guest by providing the required details needed to process, confirm, and deliver your order.",
    },
    {
      question: "Is it safe to use my banking and card details on your website?",
      answer:
        "Yes. We use trusted third-party payment gateway providers to securely process online payments. At no point do we store your full banking or card details after the order is placed. Payment processing is handled through secure payment systems designed to protect customer payment information.",
    },
    {
      question: "I am unable to complete my order on the website. What should I do?",
      answer: `Please contact our customer support team and we will assist you. Email: ${Host?.email || ""}. Customer Support: 8:00 AM to 10:00 PM Dubai Time, 7 days a week.`,
    },
    {
      question: "Are my personal details safe on the website?",
      answer:
        "We take customer privacy and data protection seriously. We do not sell customer personal information to third parties. Customer information is used to process orders, provide customer support, deliver products, improve our services, and comply with legal obligations. For more information, please refer to our Privacy Policy.",
    },
    {
      question: "How do I track my order?",
      answer: `Once your order has been shipped, we may send you a tracking number and courier tracking details by email, SMS, WhatsApp, or another available communication method. If you do not receive tracking details, please contact our customer support team with your order number at ${Host?.email || ""}.`,
    },
    {
      question: "How much does delivery cost?",
      answer: `A flat delivery fee of ${formatPrice(userData?.contant?.standard_delivery, userData?.contant?.currency, 0)} per order is charged in the cart and shown before checkout. Customers will not be charged any additional delivery, handling, or shipping fee after placing the order unless the customer expressly approves a requested change, such as a special delivery arrangement.`,
    },
    {
      question: "Can I cancel my order?",
      answer:
        "You may request cancellation before your order has been shipped or processed for dispatch. Once an order has been shipped, cancellation may no longer be possible. If the order has already shipped, please refer to our Returns, Refunds, Cancellation & Warranty Policy.",
    },
    {
      question: "Can I return an item if I change my mind?",
      answer:
        "No. We do not accept returns, refunds, or exchanges for change of mind, buyer's remorse, incorrect selection by the customer, or products ordered by mistake, unless otherwise required by applicable law or expressly stated on the product page. This does not affect your right to request support if the item received is damaged, defective, incorrect, or not as described.",
    },
    {
      question: "What should I do if I receive a damaged, defective, or incorrect item?",
      answer:
        "Please contact our customer support team within 3 days of delivery. Please provide your order number, clear photos or video of the product, photos of the outer packaging, photos of the shipping label if available, and a short description of the issue. Damaged, defective, incorrect, or missing item claims will be reviewed in accordance with our Returns, Refunds, Cancellation & Warranty Policy.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "Available payment methods will be shown at checkout before the order is placed. Payment options may include online card payment, Cash on Delivery, or other supported payment methods depending on availability.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <HelmetComponent title={"FAQs"} metaTitle={isMetaTitle} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <div className="statisc-page">
        <div className="container static-container">
          <div className="row static-row">
            <div className="col-md-10">
              <div
                className="static-container-card"
                style={{
                  textAlign: "left",
                  padding: "20px",
                  background: "#fcfcfc",
                  border: "1px solid #eee",
                  borderRadius: "7px",
                }}
              >
                <h1 style={{ padding: "20px 0px 20px 0px", fontWeight: 300, fontSize: "1.4rem" }}>
                  Frequently Asked Questions
                </h1>

                <h4>Where do you ship?</h4>
                <p>
                  We currently deliver orders within the {userData?.contant?.country} through {window.location.hostname}
                  .
                </p>
                <p>
                  If delivery to other countries is available, the available delivery country, currency, delivery fees,
                  and shipping options will be shown on the website or at checkout before the order is placed.
                </p>

                <h4>How long does it typically take for products to be dispatched?</h4>
                <p>Orders are usually processed and dispatched within 1 to 2 working days after order confirmation.</p>
                <p>Working days are Monday to Friday, excluding UAE public holidays.</p>
                <p>
                  Orders placed outside working hours, during weekends, or on UAE public holidays may be processed on
                  the next working day.
                </p>

                <h4>How long does it typically take for my purchase to arrive once it is dispatched?</h4>
                <p>
                  After dispatch, shipments are generally delivered within 2 to 8 working days, depending on the
                  delivery location, courier, product type, and operational conditions.
                </p>
                <p>The estimated total delivery time is usually 3 to 10 working days from order confirmation.</p>
                <p>
                  Some items may take longer if they require special logistics, additional handling, supplier-side
                  processing, or courier arrangements.
                </p>

                <h4>Are all products listed on your website in stock?</h4>
                <p>We make reasonable efforts to keep product availability accurate and updated on our website.</p>
                <p>
                  Products that can be added to cart are generally available for order. However, in rare cases, an item
                  may become unavailable after an order is placed due to supplier-side stock changes, fulfillment
                  limitations, system updates, or other operational reasons.
                </p>
                <p>
                  If this happens, we will contact the customer promptly and offer one or more of the following options:
                </p>
                <p>• wait for the product to become available;</p>
                <p>• choose an alternative product;</p>
                <p>• cancel the affected item and receive a full refund for that item.</p>
                <p>
                  We do not charge customers any additional amount after order confirmation without the customer's
                  express approval.
                </p>

                <h4>Do I need to open an account to shop on your website?</h4>
                <p>No. You do not need to open an account to place an order on our website.</p>
                <p>
                  You may checkout as a guest by providing the required details needed to process, confirm, and deliver
                  your order.
                </p>

                <h4>Is it safe to use my banking and card details on your website?</h4>
                <p>Yes. We use trusted third-party payment gateway providers to securely process online payments.</p>
                <p>At no point do we store your full banking or card details after the order is placed.</p>
                <p>
                  Payment processing is handled through secure payment systems designed to protect customer payment
                  information.
                </p>

                <h4>I am unable to complete my order on the website. What should I do?</h4>
                <p>Please contact our customer support team and we will assist you.</p>
                <p>Email: {Host?.email}</p>
                <p>Customer Support: 8:00 AM to 10:00 PM Dubai Time, 7 days a week</p>

                <h4>Are my personal details safe on the website?</h4>
                <p>We take customer privacy and data protection seriously.</p>
                <p>We do not sell customer personal information to third parties.</p>
                <p>
                  Customer information is used to process orders, provide customer support, deliver products, improve
                  our services, and comply with legal obligations.
                </p>
                <p>For more information, please refer to our Privacy Policy.</p>

                <h4>How do I track my order?</h4>
                <p>
                  Once your order has been shipped, we may send you a tracking number and courier tracking details by
                  email, SMS, WhatsApp, or another available communication method.
                </p>
                <p>
                  If you do not receive tracking details, please contact our customer support team with your order
                  number.
                </p>
                <p>Email: {Host?.email}</p>

                <h4>How much does delivery cost?</h4>
                <p>
                  A flat delivery fee of{" "}
                  {formatPrice(userData?.contant?.standard_delivery, userData?.contant?.currency, 0)} per order is
                  charged in the cart and shown before checkout.
                </p>
                <p>
                  Customers will not be charged any additional delivery, handling, or shipping fee after placing the
                  order unless the customer expressly approves a requested change, such as a special delivery
                  arrangement.
                </p>

                <h4>Can I cancel my order?</h4>
                <p>You may request cancellation before your order has been shipped or processed for dispatch.</p>
                <p>Once an order has been shipped, cancellation may no longer be possible.</p>
                <p>
                  If the order has already shipped, please refer to our Returns, Refunds, Cancellation & Warranty
                  Policy.
                </p>

                <h4>Can I return an item if I change my mind?</h4>
                <p>
                  No. We do not accept returns, refunds, or exchanges for change of mind, buyer's remorse, incorrect
                  selection by the customer, or products ordered by mistake, unless otherwise required by applicable law
                  or expressly stated on the product page.
                </p>
                <p>
                  This does not affect your right to request support if the item received is damaged, defective,
                  incorrect, or not as described.
                </p>

                <h4>What should I do if I receive a damaged, defective, or incorrect item?</h4>
                <p>Please contact our customer support team within 3 days of delivery.</p>
                <p>Please provide:</p>
                <p>• your order number;</p>
                <p>• clear photos or video of the product;</p>
                <p>• photos of the outer packaging;</p>
                <p>• photos of the shipping label, if available;</p>
                <p>• a short description of the issue.</p>
                <p>
                  Damaged, defective, incorrect, or missing item claims will be reviewed in accordance with our Returns,
                  Refunds, Cancellation & Warranty Policy.
                </p>

                <h4>What payment methods do you accept?</h4>
                <p>Available payment methods will be shown at checkout before the order is placed.</p>
                <p>
                  Payment options may include online card payment, Cash on Delivery, or other supported payment methods
                  depending on availability.
                </p>

                <h4>Who operates {Host?.domainName2}?</h4>
                <p>{Host?.domainName2} is operated by:</p>
                <p>{Host?.companyName}</p>
                <div style={{ fontSize: "14px" }}>
                  <strong>Trade Name: {Host?.domainName2}</strong>
                  <br />
                  <strong>Trade License: {Host?.companyName}</strong>
                  <br />
                  <strong>License Number: {Host?.licenseNumber}</strong>
                </div>
                <p style={{ lineHeight: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: Host?.address,
                    }}
                  ></div>
                  <MailOutlineIcon style={{ fontSize: "18px" }} /> {Host?.email} <br />
                  <PhoneIcon style={{ fontSize: "18px" }} /> {Host?.phone}
                </p>

                <p>Website: {window.location.hostname}</p>

                <p>&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FAQS;
